import axios from "../AxiosInterCeptor/AxiosInterCeptor";
import { ToastError, ToastSuccess } from "../UiComponents/Toaster/Toast";

const URL = process.env.React_App_BaseUrl;

// Brisk / CPA endpoints ALWAYS go to the production backend — it holds the
// external Brisk API credentials (BRISK_* env vars) that a local backend
// doesn't have. Override with REACT_APP_BRISK_API_URL in .env if needed.
const BRISK_URL =
  process.env.REACT_APP_BRISK_API_URL || "https://coreapi.rayalbrokers.com/api";

const PostCreatePolicy = (recData, options = {}) => {
  const { suppressToast = false } = options;
  return axios
    .post(`${URL}/createpolicy`, recData)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        const message =
          typeof res.message === "string"
            ? res.message
            : "Failed to create policy";
        if (!suppressToast) {
          ToastError(message);
        }
        return { error: true, message };
      }
      return res;
    })
    .catch((error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create policy";
      if (!suppressToast) {
        ToastError(message);
      }
      return { error: true, message };
    });
};

const CreateBulkPolicies = (recData) => {
  return axios
    .post(`${URL}/createBulkPolicies`, recData)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        return { error: true, message: res.message };
      }
      return res;
    })
    .catch((error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create bulk policies";
      return { error: true, message };
    });
};

/**
 * Retry PDF upload for a failed policy
 */
const RetryPolicyPDFUpload = (policyId) => {
  return axios
    .post(`${URL}/retryPolicyPDFUpload`, { policyId })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        return { error: true, message: res.message };
      }
      ToastSuccess(res.message || "PDF upload retry initiated successfully");
      return res;
    })
    .catch((error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to retry PDF upload";
      ToastError(message);
      return { error: true, message };
    });
};

/**
 * Retry failed policy (reset status to pending)
 */
export const RetryFailedPolicy = (policyNumber) => {
  return axios
    .post(`${URL}/retryFailedPolicy`, { policyNumber })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        return { error: true, message: res.message };
      }
      ToastSuccess(res.message || "Policy retry initiated successfully");
      return res;
    })
    .catch((error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to retry policy";
      ToastError(message);
      return { error: true, message };
    });
};

// Server-side switches the Online Policy form mirrors (e.g. whether the TP
// insurer can be the PA Cover company and the wallet is split per insurer).
export const GetOnlinePolicyConfig = () => {
  return axios
    .get(`${URL}/onlinePolicy-config`)
    .then((response) => response.data?.data || {})
    .catch((error) => {
      console.error("Error loading online policy config:", error);
      return null;
    });
};

// Create Online Policy API. A refusal carries the server's `code` (INSUFFICIENT_WALLET + `shortfalls`: the page then
// offers "Hold until money is added" instead of a toast; IN_PROGRESS / SUPER_ADMIN), and `noResponse` when nothing
// answered (the only case where the same create request id may be sent again).
const PostCreateOnlinePolicy = (recData, params) => {
  return axios
    .post(`${URL}/onlinePolicy`, recData, { params })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
        if (res.code !== "INSUFFICIENT_WALLET") ToastError(res.message || "Failed to create online policy");
        return { error: true, message: res.message, code: res.code, shortfalls: res.shortfalls };
      } else {
        if (!res.heldForPayment) ToastSuccess(res.message || "Online policy created successfully");
        return res;
      }
    })
    .catch((error) => {
      console.error("Error creating online policy:", error);
      const body = error?.response?.data || {};
      const errorMessage = body.message || error.message || "Failed to create online policy";
      if (body.code !== "INSUFFICIENT_WALLET") ToastError(errorMessage);
      return {
        error: true,
        message: errorMessage,
        code: body.code,
        shortfalls: body.shortfalls,
        noResponse: !error?.response || [502, 503, 504].includes(error.response.status),
      };
    });
};

// Update Online Policy API
/**
 * Attach a Brisk certificate to a policy.
 *
 * A dedicated endpoint, NOT UpdateOnlinePolicy below: that one re-queues the
 * automation job server-side (dao/onlinePolicyDao.js -> enqueuePolicyJob), so
 * using it here would re-submit a policy that has already been filed.
 */
const UpdateOnlinePolicyBriskCertificate = (policyId, certificate) => {
  return axios
    .patch(`${URL}/onlinePolicy/${policyId}/briskCertificate`, certificate)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message || "Failed to save the certificate on the policy");
        return { error: true, message: res.message };
      }
      return res;
    })
    .catch((error) => {
      const message =
        error?.response?.data?.message ||
        error.message ||
        "Failed to save the certificate on the policy";
      ToastError(message);
      return { error: true, message };
    });
};

const UpdateOnlinePolicy = (policyId, updateData) => {
  return axios
    .put(`${URL}/onlinePolicy/${policyId}`, updateData)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
        ToastError(res.message || "Failed to update online policy");
        return { error: true, message: res.message };
      } else {
        ToastSuccess(res.message || "Online policy updated successfully");
        return res;
      }
    })
    .catch((error) => {
      console.error("Error updating online policy:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to update online policy";
      ToastError(errorMessage);
      return { error: true, message: errorMessage };
    });
};

// Get Single Online Policy API
// { silent: true } = a background refresh (live updates): a failure is returned, never shown as a toast.
const GetOnlinePolicy = (policyId, { silent = false } = {}) => {
  const say = silent ? () => {} : ToastError;
  return axios
    .get(`${URL}/onlinePolicy/${policyId}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
        say(res.message || "Failed to fetch online policy");
        return { error: true, message: res.message };
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.error("Error fetching online policy:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch online policy";
      say(errorMessage);
      return { error: true, message: errorMessage };
    });
};

// Get All Online Policies API
// { silent: true } = a background refresh (live updates): a failure is returned, never shown as a toast.
const GetAllOnlinePolicies = (params, { silent = false } = {}) => {
  const say = silent ? () => {} : ToastError;
  return axios
    .get(`${URL}/onlinePolicy`, { params })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
        say(res.message || "Failed to fetch online policies");
        return { error: true, message: res.message };
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.error("Error fetching online policies:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch online policies";
      say(errorMessage);
      return { error: true, message: errorMessage };
    });
};

// Changes by Arun
const retreiveDataFromPolicy = (_id) => {
  // return axios.get(`${URL}/retrievePolicy/${_id}`).then((response) => {
  return axios
    .get(`${URL}/readPolicyFileByPolicyId/${_id}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
        return {
          error: true,
          message: res.message || "Failed to retrieve policy data",
        };
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.error("Error retrieving policy data:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to retrieve policy data";
      return { error: true, message: errorMessage };
    });
};
const formatNumber = (num) => new Intl.NumberFormat("en-IN").format(num || 0);
// Added by Arun
const getPolicyBazaarTicketNumber = (id) => {
  return axios
    .get(`${URL}/getPolicyBazaarTicketNumber/${id}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      let message = error?.response?.data || "";
      // console.error("error ----", message);
      if (message)
        ToastError(
          typeof message == "object" ? JSON.stringify(message) : message
        );
    });
};

// loginPortal added by gokul....
const LoginPortalData = (recData) => {
  return axios
    .get(`${URL}/getLoginPortal`, {
      headers: {
        companyUserId: recData.userId,
        companyId: recData.companyId,
        isActive: recData.isActive ? true : false,
      },
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    });
};

const PostSearchPolicy = (recData) => {
  return axios
    .post(`${URL}/searchpolicy`, recData)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};
// changes by somesh
const GetFilterCCEntry = async (recData) => {
  try {
    const response = await axios.post(`${URL}/filterCCEntry`, recData);
    const res = response.data;
    if (res.error) {
      console.log(res.error);
      return null;
    } else {
      return res;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
};

// changes by somesh
const unlinkTicketNumber = async (_id, recData) => {
  try {
    const response = await axios.post(
      `${URL}/unlinkTicketNumber/${_id}`,
      recData
    );
    const res = response.data;
    if (res.error) {
      ToastError(res.message);
      return null;
    } else {
      ToastSuccess(res.message);
      // console.log(res)
      return res;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
};

// changes by somesh
const GetFilterCCEntryAll = async () => {
  try {
    const response = await axios.get(`${URL}/filterCCEntryAll`);
    const res = response.data;
    console.log(res);
    if (res.error) {
      console.log(res.error);
      return null;
    } else {
      return res;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
};

// changes by somesh
const ticketAlreadyExist = async (_id, recData) => {
  try {
    const response = await axios.patch(
      `${URL}/ticketAlreadyExist/${_id}`,
      recData
    );
    const res = response.data;
    console.log(res);
    if (res.error) {
      console.log(res.error);
      return null;
    } else {
      return res;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
};

// added by gokul..
const CheckTheTickNum = async (tickNum) => {
  try {
    const result = await axios.get(`${URL}/ticketAlreadyExists/${tickNum}`);
    const res = result.data;
    if (res.error) {
      console.log(res.error);
    } else {
      return res;
    }
  } catch (err) {
    console.log(err);
  }
};

const GetPolicyList = (data) => {
  return axios
    .get(`${URL}/createpolicy`, {
      // the Log Status / Cheque Status pages ask for their own lists (undefined headers are not sent)
      headers: {
        logStatus: data?.logStatus,
        chequeStatus: data?.chequeStatus,
      },
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

const UpdatePolicyList = (_id, recData) => {
  // console.log("id : ", _id);
  // console.log("ticketNumber : ", recData);
  return axios.put(`${URL}/updatepolicy/${_id}`, recData).then((response) => {
    const res = response.data;
    if (res.error) {
      ToastError(res.message);
    } else {
      ToastSuccess(res.message);
      return res;
    }
  });
};

const DeletePolicyList = (_id) => {
  return axios
    .delete(`${URL}/deletepolicy/${_id}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message);
      } else {
        ToastError(res.message);
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

const FilterPolicyList = (recData) => {
  const { startDate, endDate, ...bodyData } = recData;

  const headers = {};
  if (startDate) {
    headers.startdate = startDate;
  }
  if (endDate) {
    headers.enddate = endDate;
  }

  return axios
    .post(`${URL}/filterPolicyList`, bodyData, { headers })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    });
};

const AutoFillPolicyDetails = (recData) => {
  return axios.post(`${URL}/verifypolicy`, recData).then((response) => {
    const res = response.data;
    if (res.error) {
      console.log(res.error);
    } else {
      return res;
    }
  });
};

const ReadPolicyFileByPolicyId = (_id) => {
  return axios
    .get(`${URL}/readPolicyFileByPolicyId/${_id}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

const SaveComissionReceivableAmount = (_id, recData) => {
  return axios
    .put(`${URL}/saveComissionReceivableAmount/${_id}`, recData)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    });
};

const GetCommisionReceivableAmount = (
  pageIndex,
  pageSize,
  companyId,
  bookingCodeId,
  startDate,
  policyNumber,
  endDate,
  filterType,
  search
) => {
  return axios
    .get(`${URL}/getComissionReceivableAmount`, {
      headers: {
        page: pageIndex,
        limit: pageSize,
        companyId: companyId,
        bookingCodeId: bookingCodeId,
        startDate: startDate,
        policyNumber: policyNumber,
        endDate: endDate,
        type: filterType,
        search: search,
        FilterType: "UTILITES_SCREEN",
        screen: "COMMISION_RECIVABLE",
      },
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

const GetPolicyFindbyId = (_id) => {
  return axios
    .get(`${URL}/getPolicyById/${_id}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

const uploadCCEntryData = async (recData) => {
  try {
    const response = await axios.post(`${URL}/uploadCCEntryData`, recData);
    const res = response.data;
    if (res.error) {
      console.log(res.error);
      return null;
    } else {
      return res;
    }
  } catch (error) {
    console.log(error);
    ToastError(error?.response?.data?.message);
    return null;
  }
};

const VerifyPolicyNumber = (data) => {
  return axios
    .get(`${URL}/verifyPolicyNumber`, {
      headers: {
        policyNumber: data,
      },
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    });
};

const GetUserpayablePercentage = (_id) => {
  return axios
    .get(`${URL}/getUserpayablePercentage/${_id}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

const GetBranchpayablePercentage = (_id) => {
  return axios
    .get(`${URL}/getBranchpayablePercentage/${_id}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

const GetReceivablePayablePercentage = (_id) => {
  return axios
    .get(`${URL}/getReceivablePercentage/${_id}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

const GetPolicyFileById = (_id) => {
  return axios
    .get(`${URL}/getPolicyFileById/${_id}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

//policy maping Api

const CreatePolicyMappingService = (recData) => {
  return axios.put(`${URL}/createPolicyMapping`, recData).then((response) => {
    const res = response.data;
    if (res.error) {
      console.log(res.error);
    } else {
      return res;
    }
  });
};

const GetPolicyMapping = (data, id, startDate = null, endDate = null) => {
  const headers = { requesttype: data, mappinguserid: id };
  if (startDate) {
    headers.startDate = startDate;
  }
  if (endDate) {
    headers.endDate = endDate;
  }
  return axios
    .get(`${URL}/getPolicyMapping`, {
      headers: headers,
      params: {
        startdate: startDate,
        enddate: endDate
      }
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    });
};

const GetPolicyMappingByPolicyID = (_id) => {
  return axios
    .get(`${URL}/getPolicyMappingByPolicyID/${_id}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    });
};

const UpdatePolicyMapping = (data) => {
  return axios.patch(`${URL}/updatePolicyMapping`, data).then((response) => {
    const res = response.data;
    if (res.error) {
      console.log(res.error);
    } else {
      return res;
    }
  });
};

const ReframeEndDateTimeFormat = (endDate) => {
  let tempEndDate = new Date(endDate);
  let reframeDate = new Date(
    tempEndDate.setHours(23, 59, 59, 59)
    // tempEndDate.getMonth(),
    // tempEndDate.getDate(),
    // 23,
    // 59,
    // 59
  );
  return reframeDate;
};

const uploadPolicyFile = async (recData) => {
  try {
    const response = await axios.post(`${URL}/uploadPolicyFile`, recData);
    const res = response.data;
    if (res.error) {
      console.log(res.error);
      return null;
    } else {
      return res;
    }
  } catch (error) {
    console.log(error);
    ToastError(error?.response?.data?.message);
    return null;
  }
};

/**
 * Hold a queued policy so the automation skips it, or release it back.
 *
 * A 409 means the race was lost — the automation claimed the job first. That is
 * not an error the user caused, so the message it carries ("Too late to hold
 * this policy — the automation has already started it") is what gets shown.
 */
const HoldOnlinePolicy = (policyId, hold) => {
  return axios
    .post(`${URL}/onlinePolicy/hold`, {
      policyId,
      hold,
      userId: sessionStorage.getItem("UserId") || null,
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message || "Failed to update the hold");
        return { error: true, message: res.message };
      }
      ToastSuccess(res.message || "Hold updated");
      return res;
    })
    .catch((error) => {
      console.error("Error updating hold on online policy:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to update the hold";
      ToastError(errorMessage);
      return {
        error: true,
        message: errorMessage,
        currentStatus: error?.response?.data?.currentStatus,
      };
    });
};

const RetryOnlinePolicy = (policyId) => {
  return axios
    .post(`${URL}/onlinePolicy/retry`, { policyId })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message || "Failed to retry online policy");
        return { error: true, message: res.message };
      } else {
        ToastSuccess(res.message || "Online policy retry triggered successfully");
        return res;
      }
    })
    .catch((error) => {
      console.error("Error retrying online policy:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to retry online policy";
      ToastError(errorMessage);
      return { error: true, message: errorMessage };
    });
};

/**
 * History dialog: set a Completed / Failed Reliance stage (proposal / kyc / payment) of one part back to Pending —
 * every later stage goes with it. run: true also queues the part again ("Update and run now"). A refusal (pending
 * stage, the automation running, paid from the wallet at Reliance) comes back as { error: true, message }.
 */
const ResetRelianceStage = ({ policyId, part, stage, run = false }) => {
  return axios
    .post(`${URL}/onlinePolicy/stage/reset`, {
      policyId,
      part,
      stage,
      run: !!run,
      userId: sessionStorage.getItem("UserId") || null,
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message || "Could not set the stage back to pending");
        return { error: true, message: res.message };
      }
      ToastSuccess(res.message || "Stage set back to pending");
      return res;
    })
    .catch((error) => {
      console.error("Error resetting a Reliance stage:", error);
      const errorMessage =
        error?.response?.data?.message || error.message || "Could not set the stage back to pending";
      ToastError(errorMessage);
      return { error: true, message: errorMessage };
    });
};

/** A step answer: the server's sentence as a toast, { error, message } on a refusal. */
const stepAnswer = (fallbackError, fallbackOk) => ({
  ok: (response) => {
    const res = response.data;
    if (res.error) {
      ToastError(res.message || fallbackError);
      return { error: true, message: res.message };
    }
    ToastSuccess(res.message || fallbackOk);
    return res;
  },
  fail: (error) => {
    const errorMessage = error?.response?.data?.message || error.message || fallbackError;
    ToastError(errorMessage);
    return { error: true, message: errorMessage };
  },
});

/**
 * Edit -> Status: set one part's steps by hand — changes = [{ stage, status: "completed" | "pending", proposalNumber?,
 * link? }], a reason (required), run: true = queue it now ("Update & run"). A refusal comes back as { error, message }.
 */
const SetStagesByHand = ({ policyId, part, changes, reason, run = false, tpPolicy = null }) => {
  const answer = stepAnswer("Could not save the steps", "Steps saved");
  return axios
    .post(`${URL}/onlinePolicy/stage/manual`, {
      policyId,
      part,
      changes,
      reason,
      run: !!run,
      // the TP part of a two-insurer policy set Completed: the TP policy's number and dates (Reliance OD waits for them)
      ...(tpPolicy ? { tpPolicy } : {}),
      userId: sessionStorage.getItem("UserId") || null,
    })
    .then(answer.ok)
    .catch(answer.fail);
};

/**
 * Edit -> TP policy details: the TP policy's number and dates of a two-insurer policy (the insurer is its TP company).
 * A Reliance OD that waited for them is queued again by the server.
 */
const SaveTpPolicyDetails = ({ policyId, policyNumber, startDate, endDate }) => {
  const answer = stepAnswer("Could not save the TP policy details", "TP policy details saved");
  return axios
    .post(`${URL}/onlinePolicy/${policyId}/tp-policy`, {
      policyNumber,
      startDate,
      endDate,
      userId: sessionStorage.getItem("UserId") || null,
    })
    .then(answer.ok)
    .catch(answer.fail);
};

/**
 * Edit -> KYC files: new Aadhaar / PAN only (multipart aadharCard / panCard, aadharNumber). Only the KYC is done
 * again — no new proposal, no wallet movement. run: true = queue it now.
 */
const UpdateKycFiles = (policyId, { aadharCard, panCard, aadharNumber, run = false }) => {
  const answer = stepAnswer("Could not save the KYC files", "KYC files saved");
  const formData = new FormData();
  if (aadharCard) formData.append("aadharCard", aadharCard);
  if (panCard) formData.append("panCard", panCard);
  if (aadharNumber) formData.append("aadharNumber", aadharNumber);
  formData.append("run", run ? "true" : "false");
  formData.append("userId", sessionStorage.getItem("UserId") || "");
  return axios
    .post(`${URL}/onlinePolicy/${policyId}/kyc-files`, formData, { headers: { "Content-Type": "multipart/form-data" } })
    .then(answer.ok)
    .catch(answer.fail);
};

// { silent: true } = a live update in the background: a failure is returned, never shown as a toast.
const GetViewErrorOnlinePolicies = (params, { silent = false } = {}) => {
  const say = silent ? () => {} : ToastError;
  return axios
    .get(`${URL}/onlinePolicy/error_message/${params?.policyId}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
        say(res.message || "Failed to fetch online policies");
        return { error: true, message: res.message };
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.error("Error fetching online policies:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch online policies";
      ToastError(errorMessage);
      return { error: true, message: errorMessage };
    });
};

/**
 * Screen recordings of a policy's automation runs.
 *
 * Returns { recordings: [{ videoUrl, attemptNumber, startedAt, durationMs,
 * sizeBytes, jobStatus, ... }] }. videoUrl is a short-lived presigned S3 link,
 * so it is fetched when the dialog opens rather than cached — and it is null
 * once S3's 10-day lifecycle rule has expired the file.
 */
const GetOnlinePolicyRecordings = (policyId) => {
  return axios
    .get(`${URL}/onlinePolicy/${policyId}/recordings`)
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error fetching policy recordings:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch the automation recordings";
      ToastError(errorMessage);
      return { success: false, error: true, message: errorMessage };
    });
};

/**
 * Saved calculation formulas — one collection, one document per formula,
 * addressed by name. `FORMULA_NAMES` lists the ones that exist today; a new
 * formula is registered on the backend and read here by its name.
 *
 * Response: { data: { name, label, values: {...} } }
 */
export const FORMULA_NAMES = {
  IDV_RANGE: "IDV_RANGE",
};

const GetFormula = (name) => {
  return axios
    .get(`${URL}/formula/${name}`)
    .then((response) => response.data)
    .catch((error) => {
      // Deliberately silent: callers fall back to their built-in defaults, so a
      // failure here must not toast at the user on page load.
      console.error(`Error fetching the ${name} formula:`, error);
      return { error: true, message: error.message };
    });
};

const UpdateFormula = (name, values) => {
  return axios
    .put(`${URL}/formula/${name}`, values)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message || "Failed to update the formula");
        return { error: true, message: res.message };
      }
      return res;
    })
    .catch((error) => {
      console.error(`Error updating the ${name} formula:`, error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to update the formula";
      ToastError(errorMessage);
      return { error: true, message: errorMessage };
    });
};

// Upload Documents (PAN and Aadhar) for Online Policy
const UploadOnlinePolicyDocuments = (policyId, formData) => {
  console.log(`📤 Uploading merged documents for policy: ${policyId}`);

  return axios
    .post(`${URL}/onlinePolicy/${policyId}/upload-documents`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
        ToastError(res.message || "Failed to upload documents");
        return { error: true, message: res.message };
      } else {
        console.log("✅ Documents uploaded successfully!");

        // Log the S3 links
        if (res.uploadedFiles) {
          console.log("\n📊 Uploaded Files:");
          console.log("=====================================");

          if (res.uploadedFiles.panCard) {
            console.log("PAN Card (Merged):");
            console.log(`  📄 File: ${res.uploadedFiles.panCard.fileName}`);
            console.log(`  🔗 Link: ${res.uploadedFiles.panCard.location}`);
            console.log(`  🔑 Key: ${res.uploadedFiles.panCard.key}`);
          }

          if (res.uploadedFiles.aadharCard) {
            console.log("Aadhar Card (Merged):");
            console.log(`  📄 File: ${res.uploadedFiles.aadharCard.fileName}`);
            console.log(`  🔗 Link: ${res.uploadedFiles.aadharCard.location}`);
            console.log(`  🔑 Key: ${res.uploadedFiles.aadharCard.key}`);
          }

          console.log("=====================================\n");
        }

        ToastSuccess(res.message || "Documents uploaded successfully");
        return res;
      }
    })
    .catch((error) => {
      console.error("❌ Error uploading documents:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to upload documents";
      ToastError(errorMessage);
      return { error: true, message: errorMessage };
    });
};

// Get bulk uploaded policies grouped by BulkUploadExecelFile _id
const GetBulkUploadedPolicies = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.search) queryParams.append("search", params.search);
  if (params.companyId) queryParams.append("companyId", params.companyId);
  if (params.uploadStartDate)
    queryParams.append("uploadStartDate", params.uploadStartDate);
  if (params.uploadEndDate)
    queryParams.append("uploadEndDate", params.uploadEndDate);
  if (params.issueStartDate)
    queryParams.append("issueStartDate", params.issueStartDate);
  if (params.issueEndDate)
    queryParams.append("issueEndDate", params.issueEndDate);
  if (params.unmatchedOnly)
    queryParams.append("unmatchedOnly", params.unmatchedOnly);
  if (params.sortBy) queryParams.append("sortBy", params.sortBy);
  if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
  if (params.pdfUploaded) queryParams.append("pdfUploaded", params.pdfUploaded);
  if (params.pdfFailed) queryParams.append("pdfFailed", params.pdfFailed);

  return axios
    .get(`${URL}/getBulkUploadedPolicies?${queryParams.toString()}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message || "Failed to fetch bulk uploaded policies");
        return { error: true, message: res.message };
      }
      return res;
    })
    .catch((error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch bulk uploaded policies";
      ToastError(errorMessage);
      return { error: true, message: errorMessage };
    });
};

// Download policies with missing fields
const DownloadBulkUploadedPoliciesWithMissingFields = (bulkUploadFileId) => {
  return axios
    .get(
      `${URL}/downloadBulkUploadedPoliciesWithMissingFields/${bulkUploadFileId}`,
      {
        responseType: "blob",
      }
    )
    .then((response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `BulkUpload_MissingFields_${bulkUploadFileId}_${Date.now()}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      ToastSuccess("File downloaded successfully");
      return { error: false };
    })
    .catch((error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to download file";
      ToastError(errorMessage);
      return { error: true, message: errorMessage };
    });
};

// Import and update bulk uploaded policies
const ImportAndUpdateBulkUploadedPolicies = (bulkUploadFileId, file) => {
  const formData = new FormData();
  formData.append("excelFile", file);
  formData.append("bulkUploadFileId", bulkUploadFileId);

  return axios
    .post(`${URL}/importAndUpdateBulkUploadedPolicies`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message || "Failed to import and update policies");
        return { error: true, message: res.message };
      }
      ToastSuccess(res.message || "Policies updated successfully");
      return res;
    })
    .catch((error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to import and update policies";
      ToastError(errorMessage);
      return { error: true, message: errorMessage };
    });
};

// created by AI Assistant
// Save or update PDF upload status
const SavePdfUploadStatus = (statusData) => {
  return axios
    .post(`${URL}/pdfUploadStatus`, statusData)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.error("Error saving PDF upload status:", res.message);
        return { error: true, message: res.message };
      }
      return res;
    })
    .catch((error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to save PDF upload status";
      console.error("Error saving PDF upload status:", errorMessage);
      return { error: true, message: errorMessage };
    });
};

// created by AI Assistant
// Get PDF upload status
const GetPdfUploadStatus = () => {
  return axios
    .get(`${URL}/pdfUploadStatus`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.error("Error getting PDF upload status:", res.message);
        return { error: true, message: res.message };
      }
      return res;
    })
    .catch((error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to get PDF upload status";
      console.error("Error getting PDF upload status:", errorMessage);
      return { error: true, message: errorMessage };
    });
};

const RetryBulkPdfUpload = (bulkUploadFileId) => {
  return axios
    .post(`${URL}/retryBulkPdfUpload`, { bulkUploadFileId })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        return { error: true, message: res.message };
      }
      ToastSuccess(res.message || "Bulk retry initiated successfully");
      return res;
    })
    .catch((error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to retry bulk Pdfs";
      ToastError(message);
      return { error: true, message };
    });
};
// Brisk Certificate Services
const briskMakeList = () => {
  return axios
    .get(`${BRISK_URL}/briskMakeList`)
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to fetch make list";
      ToastError(message);
      return { error: true, message };
    });
};

/**
 * Models for a make.
 *
 * The make goes in the PATH, not a query string: the live API is
 * `/briskModelList/DUCATI`. It was `?make=DUCATI` here, which returns nothing —
 * so the Model dropdown on /briskCreateCertificate stayed empty while the
 * CPA/RSA page worked, because that one imports the correct copy of this
 * function from Service/girnarApi.js.
 *
 * encodeURIComponent because makes contain spaces ("HERO MOTOCORP LTD").
 */
const briskModelList = (make) => {
  return axios
    .get(`${BRISK_URL}/briskModelList/${encodeURIComponent(make || "")}`)
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to fetch model list";
      ToastError(message);
      return { error: true, message };
    });
};

const briskPlanList = () => {
  return axios
    .get(`${BRISK_URL}/briskPlanList`)
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to fetch plan list";
      ToastError(message);
      return { error: true, message };
    });
};

const getBriskWalletAmount = () => {
  return axios
    .get(`${URL}/getBriskWalletAmount`)
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to fetch wallet amount";
      ToastError(message);
      return { error: true, message };
    });
};

const createBriskCertificate = (data) => {
  return axios
    .post(`${URL}/createBriskCertificate`, data)
    .then((response) => {
      const res = response.data;
      if (!res.error) {
        ToastSuccess(res.message || "Certificate created successfully");
      }
      return res;
    })
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to create certificate";
      ToastError(message);
      return { error: true, message };
    });
};

const saveBriskCertificate = (data) => {
  return axios
    .post(`${URL}/saveBriskCertificate`, data)
    .then((response) => {
      const res = response.data;
      if (!res.error) {
        ToastSuccess(res.message || "Certificate saved successfully");
      }
      return res;
    })
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to save certificate";
      ToastError(message);
      return { error: true, message };
    });
};

// Wallet service (if not already present)
const addWalletAmount = (data) => {
  return axios
    .post(`${URL}/addWalletAmount`, data)
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to add wallet amount";
      ToastError(message);
      return { error: true, message };
    });
};




const getCPAList = (data) => {
  return axios
    .get(`${URL}/cpaList`, { params: data })
    .then((response) => response.data)
    .catch((error) => {
      if (error.response.status == 401) {
        // console.log("error ", error);
        window.alert("Your session has been expired. Please login again.");
        window.location.href = "/login";
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("userId");
        sessionStorage.removeItem("role");
      }
      throw error;
    });
};

// Brisk Wallet Service Functions
const getBriskWallet = (status, userId = null) => {
  let url = `${URL}/getBriskWallet/${status}`;

  // Add userId as query parameter if provided
  if (userId) {
    url += `?userId=${userId}`;
  }

  return axios
    .get(url)
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to fetch wallet transactions";
      ToastError(message);
      return { error: true, message };
    });
};

const getBriskWalletReport = (data) => {
  return axios
    .get(`${URL}/getBriskWalletReport/`, {
      headers: {
        userref: data.userRef || "",
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to fetch wallet report";
      ToastError(message);
      return { error: true, message };
    });
};

const getBriskWalletReportByUserId = (userId) => {
  return axios
    .get(`${URL}/getBriskWalletReportByUserId/${userId}`)
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to fetch user wallet report";
      ToastError(message);
      return { error: true, message };
    });
};

const updateBriskWalletByID = (id, status) => {
  return axios
    .patch(`${URL}/updateBriskWalletByID/${id}/${status}`)
    .then((response) => {
      const res = response.data;
      if (!res.error) {
        ToastSuccess(res.message || "Wallet status updated successfully");
      }
      return res;
    })
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to update wallet status";
      ToastError(message);
      return { error: true, message };
    });
};

const addBriskWalletAmount = (data) => {
  return axios
    .post(`${URL}/addBriskWalletAmount`, data)
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to add wallet amount";
      ToastError(message);
      return { error: true, message };
    });
};

const getBriskWalletTransStatus = (orderId) => {
  return axios
    .get(`${URL}/briskWalletTransStatus/${orderId}`)
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to fetch transaction status";
      ToastError(message);
      return { error: true, message };
    });
};

const updateBriskPaymentScreenshot = (id, formData) => {
  return axios
    .put(`${URL}/updateBriskPaymentScreenshot/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      const res = response.data;
      if (!res.error) {
        ToastSuccess(res.message || "Screenshot uploaded successfully");
      }
      return res;
    })
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to upload screenshot";
      ToastError(message);
      return { error: true, message };
    });
};

const getReceiptImage = (key) => {
  return axios
    .get(`${URL}/getReceiptImage`, { params: { key } })
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to fetch receipt image";
      ToastError(message);
      return { error: true, message };
    });
};


// fileName = the link saves the file under this name; view = the link shows the file in the tab (backend shared/documentLink.js)
const GetDocumentPresignedUrl = (key, { fileName, view } = {}) => {
  return axios
    .post(`${URL}/onlinePolicy/presigned-url`, { key, ...(fileName ? { fileName } : {}), ...(view ? { view: true } : {}) })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        return { error: true, message: res.message };
      }
      return res;
    })
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to get presigned URL";
      return { error: true, message };
    });
};


// Edits one already saved entry of the Transaction History. _id is the commisionReceivableTransaction id (not the policy id) -
// the backend moves the policy's Received / Pending amounts by the difference. Resolves the server's answer, errors included.
const UpdateComissionReceivableTransaction = (_id, recData) => {
  return axios
    .put(`${URL}/updateComissionReceivableTransaction/${_id}`, recData)
    .then((response) => response.data)
    .catch((error) => ({
      error: true,
      message: error?.response?.data?.message || error?.message || "Failed to update the transaction",
    }));
};

// Cancels an approved policy (admin login). Resolves the server's answer, errors included, so the dialog can show the reason.
const cancelpolicyByPolicyID = (_id, data) => {
  return axios
    .patch(`${URL}/cancelpolicy/${_id}`, data)
    .then((response) => response.data)
    .catch((error) => ({
      error: true,
      message: error?.response?.data?.message || error?.message || "Failed to cancel the policy",
    }));
};

// User Additional: uploads the sheet (+ company, bank, remarks and the per-user summary) and lists the uploaded policies.
const uploadReceivableBulkUploadData = async (recData) => {
  try {
    const response = await axios.post(`${URL}/uploadReceivableData`, recData);
    const res = response.data;
    if (res.error) {
      ToastError(res.message);
      return null;
    }
    return res;
  } catch (error) {
    ToastError(error?.response?.data?.message || "Upload failed");
    return null;
  }
};

const getReceivablePolicyData = (params = {}) => {
  return axios
    .get(`${URL}/getReceivablePolicyData`, { params })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.error);
      } else {
        return res;
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

// Receivable Status: received commission by sheet (verify = read only, upload = the writer)
const VerifyBulkCommisionUpload = (formData) => {
  return axios
    .post(`${URL}/verifyBulkCommisionUpload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        return { error: true, message: res.message };
      }
      return res;
    })
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Verification failed";
      return { error: true, message };
    });
};

const BulkUploadCommision = (formData) => {
  return axios
    .post(`${URL}/bulkUploadCommision`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        return { error: true, message: res.message };
      }
      return res;
    })
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Upload failed";
      return { error: true, message };
    });
};

/**
 * Master Report "Policy Files": every requested policy PDF as one zip.
 * `options.statuses` widens the policy statuses the backend will hand back; omitting it keeps the backend default
 * (approved policies only). Resolves { error, blob } - the caller decides how to save it.
 */
const DownloadPolicyFilesZip = (policyIds, options = {}) => {
  const { signal, onDownloadProgress, statuses } = options;
  return axios
    .post(
      `${URL}/downloadPolicyFilesZip`,
      { policyIds, ...(statuses?.length > 0 && { statuses }) },
      { responseType: "blob", signal, onDownloadProgress }
    )
    .then((response) => ({ error: false, blob: response.data }))
    .catch(async (error) => {
      if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED" || axios.isCancel?.(error)) {
        return { error: true, cancelled: true, message: "Cancelled" };
      }
      // A failed blob request carries its JSON error inside the blob body.
      let message = error?.message || "Failed to download policy files";
      try {
        const data = error?.response?.data;
        if (data && typeof data.text === "function") {
          const parsed = JSON.parse(await data.text());
          if (parsed?.message) message = parsed.message;
        }
      } catch (e) {}
      return { error: true, message };
    });
};

export {
  PostSearchPolicy,
  UpdateComissionReceivableTransaction,
  cancelpolicyByPolicyID,
  uploadReceivableBulkUploadData,
  getReceivablePolicyData,
  VerifyBulkCommisionUpload,
  BulkUploadCommision,
  DownloadPolicyFilesZip,
  LoginPortalData, //added by gokul..
  GetPolicyList,
  PostCreatePolicy,
  PostCreateOnlinePolicy,
  UpdateOnlinePolicy,
  UpdateOnlinePolicyBriskCertificate,
  GetOnlinePolicy,
  GetAllOnlinePolicies,
  UpdatePolicyList,
  DeletePolicyList,
  FilterPolicyList,
  AutoFillPolicyDetails,
  SaveComissionReceivableAmount,
  GetCommisionReceivableAmount,
  GetPolicyFindbyId,
  VerifyPolicyNumber,
  GetUserpayablePercentage,
  GetBranchpayablePercentage,
  GetReceivablePayablePercentage,
  GetPolicyFileById,
  GetFilterCCEntry,
  unlinkTicketNumber,
  GetFilterCCEntryAll,
  ticketAlreadyExist,
  CheckTheTickNum,
  CreatePolicyMappingService,
  GetPolicyMapping,
  GetPolicyMappingByPolicyID,
  UpdatePolicyMapping,
  ReadPolicyFileByPolicyId,
  ReframeEndDateTimeFormat,
  getPolicyBazaarTicketNumber,
  retreiveDataFromPolicy,
  uploadPolicyFile,
  formatNumber,
  GetViewErrorOnlinePolicies,
  GetFormula,
  UpdateFormula,
  GetOnlinePolicyRecordings,
  UploadOnlinePolicyDocuments,
  GetBulkUploadedPolicies,
  DownloadBulkUploadedPoliciesWithMissingFields,
  ImportAndUpdateBulkUploadedPolicies,
  CreateBulkPolicies,
  RetryPolicyPDFUpload,
  SavePdfUploadStatus,
  GetPdfUploadStatus,
  RetryBulkPdfUpload,
  RetryOnlinePolicy,
  ResetRelianceStage,
  SetStagesByHand,
  SaveTpPolicyDetails,
  UpdateKycFiles,
  HoldOnlinePolicy,
  uploadCCEntryData,
  briskMakeList,
  briskModelList,
  briskPlanList,
  getBriskWalletAmount,
  createBriskCertificate,
  saveBriskCertificate,
  addWalletAmount,
  getCPAList,
  // Brisk Wallet exports
  getBriskWallet,
  getBriskWalletReport,
  getBriskWalletReportByUserId,
  updateBriskWalletByID,
  addBriskWalletAmount,
  getBriskWalletTransStatus,
  updateBriskPaymentScreenshot,
  getReceiptImage,
  GetDocumentPresignedUrl,
};

